import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { checkDocument, fetchCategoryAverages } from '../../utils/apiServices';
import { AuthContext } from '../../contexts/AuthContext';

const DocumentVerification = ({ setUserData, setCategoryData, setIsDocumentVerified }) => {
  const { user } = useContext(AuthContext);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchResults = async () => {
      try {
        // Verificamos si el usuario está autenticado
        if (user && user.document) {
          // Obtenemos los datos del usuario
          const response = await checkDocument(user.document);
          console.log('API Response:', response);

          // Verificamos si el documento existe
          if (response.exists) {
            setUserData({ ...response.data, id: response.id, createdAt: response.data.created_at });

            // Obtenemos los promedios de categorías
            const categoryResponse = await fetchCategoryAverages(user.document);

            // Verificamos que la respuesta de la API para las categorías también sea correcta
            if (categoryResponse.exists) {
              setCategoryData(categoryResponse.category_averages);
            }

            setIsDocumentVerified(true);
            setErrors({});
          } else {
            setErrors({ document: 'No has realizado el autodiagnóstico.' });
          }
        } else {
          setErrors({ document: 'Usuario no autenticado.' });
        }
      } catch (error) {
        console.error('Error al obtener los resultados:', error);

        // Capturamos el error si la respuesta no es JSON válido
        if (error.message.includes('Unexpected token')) {
          setErrors({ document: 'La respuesta del servidor no es válida. Por favor, intente nuevamente más tarde.' });
        } else {
          setErrors({ document: 'Error al obtener los resultados.' });
        }
      } finally {
        setIsSubmitting(false);
      }
    };

    fetchResults();
  }, [user, setUserData, setCategoryData, setIsDocumentVerified]);

  const handleAutodiagnosticoClick = () => {
    navigate('/diagnosticos');
  };

  if (isSubmitting) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: 'linear-gradient(135deg, #f4f7fc 0%, #e8f0fe 100%)' }}>
        <div
          className="border-[5px] rounded-full animate-spin"
          style={{
            width: '70px',
            height: '70px',
            borderColor: '#e8f0fe',
            borderTopColor: 'var(--color-azul-icetex)',
          }}
        ></div>
        <p className="text-texto-sec font-semibold" style={{ marginTop: '25px', fontSize: '1.2rem' }}>
          Cargando tus resultados...
        </p>
      </div>
    );
  }

  if (errors.document) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #f4f7fc 0%, #e8f0fe 100%)', padding: '40px 20px' }}
      >
        <div className="w-full animate-fade-in" style={{ maxWidth: '700px' }}>
          {/* Header */}
          <div style={{ marginBottom: '30px' }}>
            <Link
              to="/"
              className="inline-flex items-center bg-transparent text-icetex border-0 font-semibold cursor-pointer no-underline transition-all duration-300 hover:bg-white"
              style={{ gap: '8px', padding: '12px 20px', borderRadius: '50px', fontSize: '1rem' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
              Volver al Inicio
            </Link>
          </div>

          {/* Error Card */}
          <div
            className="bg-white text-center"
            style={{ borderRadius: '30px', padding: '60px 50px', boxShadow: '0 20px 60px rgba(0, 63, 129, 0.15)' }}
          >
            {/* Error Icon */}
            <div
              className="flex items-center justify-center mx-auto animate-pulse-scale"
              style={{
                width: '120px',
                height: '120px',
                marginBottom: '30px',
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                borderRadius: '50%',
                boxShadow: '0 10px 30px rgba(238, 90, 111, 0.3)',
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--color-blanco)' }}>
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>

            <h1
              className="text-texto-oscuro font-extrabold"
              style={{ fontSize: '2.2rem', marginBottom: '20px' }}
            >
              No se encontraron resultados
            </h1>
            <p
              className="text-texto-sec mx-auto"
              style={{ fontSize: '1.1rem', lineHeight: '1.8', marginBottom: '40px', maxWidth: '500px' }}
            >
              {errors.document}
            </p>

            {/* Actions */}
            <div className="flex justify-center flex-wrap" style={{ gap: '20px' }}>
              <button
                type="button"
                className="border-0 font-bold cursor-pointer no-underline uppercase inline-flex items-center text-white transition-all duration-300 hover:-translate-y-[3px]"
                onClick={handleAutodiagnosticoClick}
                style={{
                  gap: '10px',
                  padding: '16px 40px',
                  borderRadius: '50px',
                  fontSize: '1rem',
                  letterSpacing: '0.5px',
                  background: 'linear-gradient(135deg, var(--color-azul-icetex) 0%, #0056b3 100%)',
                  boxShadow: '0 6px 20px rgba(0, 63, 129, 0.3)',
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="12" y1="18" x2="12" y2="12"></line>
                  <line x1="9" y1="15" x2="15" y2="15"></line>
                </svg>
                Realizar Autodiagnóstico
              </button>
              <Link
                to="/"
                className="font-bold cursor-pointer no-underline uppercase inline-flex items-center bg-white text-icetex transition-all duration-300 hover:bg-icetex hover:text-white hover:-translate-y-[3px]"
                style={{
                  gap: '10px',
                  padding: '16px 40px',
                  borderRadius: '50px',
                  fontSize: '1rem',
                  letterSpacing: '0.5px',
                  border: '2px solid var(--color-azul-icetex)',
                }}
              >
                Volver al Inicio
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Si todo va bien, no renderizamos nada aquí ya que `setIsDocumentVerified` será `true`
  return null;
};

export default DocumentVerification;
